/** @jsxImportSource @emotion/react */
import { Button } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { Cell, Value } from '../Cell'
import * as classes from './styles'

export type RowProps = {
  values: Value[]
  onChange: (values: Value[]) => void
  onRemove: () => void
  isColumnsEditable: boolean
  isEditable: boolean
  onIsEditableChange: (isEditable: boolean) => void
  isError?: boolean
  isEmpty: boolean
  isSortable: boolean
}

export const Row = ({
  values,
  onChange,
  onRemove,
  isColumnsEditable,
  isEditable,
  onIsEditableChange,
  isError,
  isEmpty,
  isSortable,
}: RowProps) => {
  const [tempValues, setTempValues] = useState(values)

  useEffect(() => {
    setTempValues(values)
  }, [values])

  const handleChangeTempValue = useCallback(
    (index: number) => (value: Value) => {
      setTempValues((prevValues) => {
        const values = [...prevValues]
        values[index] = value
        return values
      })
    },
    []
  )

  const handleCancel = useCallback(() => {
    setTempValues(values)
    onIsEditableChange(false)
  }, [values, onIsEditableChange])

  const handleConfirm = useCallback(() => {
    onChange(tempValues)
    onIsEditableChange(false)
  }, [tempValues, onChange, onIsEditableChange])

  return (
    <div css={classes.row(isError)} data-testid="row">
      <div css={classes.columns(tempValues.length)}>
        {tempValues.map((value, index) => (
          <Cell
            value={value}
            onChange={handleChangeTempValue(index)}
            isEditable={isEditable}
            key={index}
            data-testid={`cell-${index}`}
          />
        ))}
      </div>
      <div css={classes.buttons(isColumnsEditable)}>
        {(!isEditable || isEmpty) && (
          <Button
            variant="outlined"
            color="red"
            size="small"
            onClick={onRemove}
            css={classes.iconButton}
            data-testid="row-delete-button"
          >
            <TrashIcon />
          </Button>
        )}
        {!isEditable && (
          <Button
            variant="outlined"
            color="gray"
            size="small"
            onClick={() => onIsEditableChange(true)}
            css={classes.iconButton}
            data-testid="row-edit-button"
          >
            <EditIcon />
          </Button>
        )}
        {isEditable && (
          <>
            {!isEmpty && (
              <Button
                variant="outlined"
                color="red"
                size="small"
                onClick={handleCancel}
                css={classes.iconButton}
                data-testid="row-cross-button"
              >
                <CrossIcon />
              </Button>
            )}
            <Button
              variant="outlined"
              color="green"
              size="small"
              onClick={handleConfirm}
              css={classes.iconButton}
              data-testid="row-check-button"
            >
              <CheckIcon />
            </Button>
          </>
        )}
      </div>
      {isSortable && <DnDIcon css={classes.dndIcon} />}
    </div>
  )
}
